import { api, ListDateFilter, ListPager, ListToolbar } from '@bemor/web-kit';
import {
    Button,
    DateTime,
    type ListViewPreset,
    PageHeader,
} from '@bemor/web-kit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import type { Notification } from '@/api/types';
import { adminListPrefetch, filterDefs, useAdminList } from '@/lib/lists';
import { useWrite } from '@/lib/queries';

const PATH = '/staff/notifications';
const PAGE_KEY = 'notifications';

/** The feed's two tabs, now views: everything, or what is still unread. */
export const NOTIFICATION_PRESETS: ListViewPreset[] = [
    { key: 'all', name: 'All', filters: {} },
    { key: 'unread', name: 'Unread', filters: { read: 'false' } },
];

/** The route's prefetch: the person's views, then the page of the feed they land on (BM-46 §2.0 + §2.4). */
export const notificationsPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: NOTIFICATION_PRESETS,
});

const notificationFilters = filterDefs(PATH, { range: 'Received' });

export function NotificationsPage() {
    // A feed, not a table: the views, the date filter and the pages come from the list layer; there are no columns.
    const { state, query } = useAdminList<Notification>(PAGE_KEY, PATH, {
        columns: [],
        filters: notificationFilters,
        presets: NOTIFICATION_PRESETS,
    });
    const client = useQueryClient();
    const invalidateBell = () =>
        client.invalidateQueries({ queryKey: ['notifications'] });
    const readAll = useWrite(
        async () => (await api().put('/staff/notifications/read-all')).data,
        ['/staff/notifications'],
    );
    const readOne = useWrite(
        async (n: Notification) =>
            (await api().put(`/staff/notifications/${n.id}/read`)).data,
        ['/staff/notifications'],
    );
    const items = query.rows;
    return (
        <div>
            <PageHeader
                title="Notifications"
                primaryAction={
                    <Button
                        variant="outline"
                        onClick={() =>
                            readAll.mutate(undefined, {
                                onSuccess: invalidateBell,
                            })
                        }
                    >
                        Mark all read
                    </Button>
                }
            />
            <ListToolbar
                className="mb-3"
                state={state}
                exportable={false}
                controls={{
                    range: <ListDateFilter state={state} label="Received" />,
                }}
            />
            <ul
                className="divide-y rounded-md border"
                data-testid="notification-feed"
            >
                {items.map((n) => (
                    <li
                        key={n.id}
                        className={`flex flex-col gap-1 p-3 text-sm md:flex-row md:items-start md:gap-4 ${n.read_at ? '' : 'bg-accent/40'}`}
                    >
                        <div className="min-w-0 flex-1">
                            <p className="font-medium">{n.title}</p>
                            <p className="text-muted-foreground">{n.body}</p>
                            {n.cta?.url ? (
                                <Link
                                    to={n.cta.url.replace(
                                        /^https?:\/\/[^/]+/,
                                        '',
                                    )}
                                    className="text-xs underline"
                                >
                                    {n.cta.label}
                                </Link>
                            ) : null}
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            <DateTime value={n.created_at} empty="—" />
                            {n.read_at ? null : (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                        readOne.mutate(n, {
                                            onSuccess: invalidateBell,
                                        })
                                    }
                                >
                                    Mark read
                                </Button>
                            )}
                        </div>
                    </li>
                ))}
                {!query.loading && items.length === 0 ? (
                    <li className="text-muted-foreground p-6 text-center">
                        Nothing to show.
                    </li>
                ) : null}
            </ul>
            <ListPager
                className="mt-3"
                state={state}
                rows={items.length}
                total={query.total}
                capped={query.capped}
                disabled={query.fetching}
            />
        </div>
    );
}
